import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  CreditCard,
  TrendingUp,
  Mail,
  DollarSign
} from 'lucide-react';

const SessionCompletionModal = ({
  open,
  onClose,
  session,
  onConfirm,
  loading = false
}) => {
  const [notes, setNotes] = useState('');

  if (!session) return null;

  const otherPerson = session.professionalId?.name || session.seekerId?.name || 'the participant';

  const handleConfirm = () => {
    onConfirm(notes);
  };

  const handleClose = () => {
    if (!loading) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle size={24} color="#10b981" />
          <Typography variant="h6" component="span" fontWeight={600}>
            Mark Session as Completed?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You're about to mark your coffee chat with <strong>{otherPerson}</strong> as completed.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          This action will capture the payment and finalize the session.
        </Alert>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          What happens next:
        </Typography>

        <List dense sx={{ mb: 3 }}>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CreditCard size={18} color="#8b5cf6" />
            </ListItemIcon>
            <ListItemText
              primary="Payment will be captured immediately"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <DollarSign size={18} color="#8b5cf6" />
            </ListItemIcon>
            <ListItemText
              primary="Your earnings (90%) will be added to your balance"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <TrendingUp size={18} color="#8b5cf6" />
            </ListItemIcon>
            <ListItemText
              primary="Funds will be included in your next payout (Mondays)"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Mail size={18} color="#8b5cf6" />
            </ListItemIcon>
            <ListItemText
              primary="Both parties will receive confirmation emails"
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </List>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Session Notes (Optional)"
          placeholder="Add any notes about this session..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={loading}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5
            }
          }}
          helperText={`${notes.length}/500 characters`}
          inputProps={{ maxLength: 500 }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          💡 These notes are private and only visible to you.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.5,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.5,
            px: 3,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
            }
          }}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle size={18} />}
        >
          {loading ? 'Completing...' : 'Confirm Completion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionCompletionModal;
